import React from 'react';
import { Palette, Type, Eye, Maximize2, RotateCcw } from 'lucide-react';

export const COLOR_SCHEMES = {
  default: { name: 'Default', colors: ['#2ECC71','#E74C3C','#3498DB','#F39C12','#9B59B6','#1ABC9C','#E67E22','#C0392B','#2980B9','#8E44AD'] },
  sunset: { name: 'Sunset', colors: ['#FF6B6B','#FFA07A','#FFD93D','#FF8C42','#E85D75','#C44569','#F78FB3','#F19066','#E66767','#FC5C65'] },
  ocean: { name: 'Cool Ocean', colors: ['#0077B6','#00B4D8','#90E0EF','#48CAE4','#023E8A','#0096C7','#ADE8F4','#CAF0F8','#0077B6','#005F73'] },
  forest: { name: 'Forest', colors: ['#2D6A4F','#52B788','#95D5B2','#40916C','#1B4332','#74C69D','#B7E4C7','#D8F3DC','#2D6A4F','#3A7D5C'] },
  neon: { name: 'Neon', colors: ['#FF006E','#3A86FF','#FFBE0B','#FB5607','#8338EC','#FF006E','#06D6A0','#118AB2','#EF476F','#FFD166'] },
  earth: { name: 'Earth', colors: ['#8B5E3C','#A0522D','#DEB887','#D2691E','#BC8F8F','#CD853F','#C4A882','#B8860B','#A0522D','#8B4513'] },
  pastel: { name: 'Pastel', colors: ['#A8DADC','#F1FAEE','#FFB4A2','#E5989B','#B5838D','#CDB4DB','#FFC8DD','#FFAFCC','#BDE0FE','#A2D2FF'] },
};

export const FONT_SIZES = [8, 9, 10, 11, 12, 14, 16, 18];

export default function GraphSidebar({
  colorScheme = 'default',
  onColorSchemeChange,
  fontSize = 11,
  onFontSizeChange,
  viewMode = '3d',
  onViewModeChange,
  show2D3DToggle = false,
  onReset,
}) {
  return (
    <div className="w-full bg-[#F5F4F2] rounded-xl border border-[#E8E6E1] p-3 space-y-4" data-testid="graph-sidebar">
      {/* Color Scheme */}
      <div>
        <label className="flex items-center gap-1.5 text-xs font-semibold text-[#2C2A28] uppercase tracking-wider mb-2">
          <Palette size={12} /> Color Theme
        </label>
        <div className="grid grid-cols-2 gap-1.5">
          {Object.entries(COLOR_SCHEMES).map(([key, scheme]) => (
            <button
              key={key}
              onClick={() => onColorSchemeChange?.(key)}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs transition border ${
                colorScheme === key
                  ? 'border-[#6B8E7B] bg-[#EAF0EC] font-semibold text-[#2C2A28]'
                  : 'border-transparent hover:bg-white text-[#5E5A56]'
              }`}
              data-testid={`color-scheme-${key}`}
            >
              <div className="flex gap-0.5">
                {scheme.colors.slice(0, 4).map((c, i) => (
                  <div key={i} className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c }} />
                ))}
              </div>
              <span className="truncate">{scheme.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Font Size */}
      <div>
        <label className="flex items-center gap-1.5 text-xs font-semibold text-[#2C2A28] uppercase tracking-wider mb-2">
          <Type size={12} /> Label Size: {fontSize}px
        </label>
        <input
          type="range"
          min="8"
          max="18"
          step="1"
          value={fontSize}
          onChange={(e) => onFontSizeChange?.(parseInt(e.target.value))}
          className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
          style={{ background: 'linear-gradient(90deg, #6B8E7B, #2ECC71)' }}
          data-testid="font-size-slider"
        />
        <div className="flex justify-between text-[10px] text-[#8A8580] mt-0.5">
          <span>8px</span><span>18px</span>
        </div>
      </div>

      {/* 2D/3D Toggle */}
      {show2D3DToggle && (
        <div>
          <label className="flex items-center gap-1.5 text-xs font-semibold text-[#2C2A28] uppercase tracking-wider mb-2">
            <Eye size={12} /> View Mode
          </label>
          <div className="flex rounded-lg overflow-hidden border border-[#E8E6E1]">
            <button
              onClick={() => onViewModeChange?.('2d')}
              className={`flex-1 px-3 py-2 text-xs font-semibold transition ${
                viewMode === '2d' ? 'bg-[#6B8E7B] text-white' : 'bg-white text-[#5E5A56] hover:bg-[#F9F8F6]'
              }`}
              data-testid="view-mode-2d"
            >
              2D View
            </button>
            <button
              onClick={() => onViewModeChange?.('3d')}
              className={`flex-1 px-3 py-2 text-xs font-semibold transition ${
                viewMode === '3d' ? 'bg-[#6B8E7B] text-white' : 'bg-white text-[#5E5A56] hover:bg-[#F9F8F6]'
              }`}
              data-testid="view-mode-3d"
            >
              3D View
            </button>
          </div>
        </div>
      )}

      {/* Reset */}
      <button
        onClick={onReset}
        className="w-full flex items-center justify-center gap-1.5 text-xs text-[#8A8580] hover:text-[#2C2A28] py-1.5 transition"
        data-testid="graph-reset-btn"
      >
        <RotateCcw size={12} /> Reset to Defaults
      </button>
    </div>
  );
}
